import '../../lib/vendor/bala.DualSelectList/js/bala.DualSelectList';
import '../../lib/vendor/bala.DualSelectList/css/bala.DualSelectList.css';
import {initLucideIcons} from '../lucide-icons';

type DualListItem = {
    id: string;
    name: string;
};

type SettingsUserConfig = {
    notSelectedColumns: DualListItem[];
    selectedColumns: DualListItem[];
    gridColumns: DualListItem[];
    gridOtherColumns: DualListItem[];
    urls: {
        saveDisplay: string;
        saveFilterSettings: string;
        setFilterSettingsToDefault: string;
    };
    labels: {
        moveAll: string;
        columnsToDisplay: string;
        displayedColumns: string;
    };
    constants: {
        refreshInterval: string;
        showRefreshCounter: string;
    };
    noColsSelected: string;
    filterNameRequired: string;
    filterNameDuplicate: string;
};

type DualSelectListApi = {
    getSelection(): DualListItem[];
    getCandidates(): DualListItem[];
    setCandidate(items?: DualListItem[]): void;
    setSelection(items?: DualListItem[]): void;
};

type FilterSettingsRow = {
    originalName: string;
    name: string;
    show: 'list' | 'button';
    color: string | null;
};

const DUAL_SELECT_API_DATA_KEY = 'naisDualSelectListApi';

/** Reload list page when side dialog closes after a successful save. */
let reloadOnSettingsClose = false;
let settingsCloseHookRegistered = false;

declare global {
    interface JQuery {
        DualSelectList(options: Record<string, unknown>): DualSelectListApi;
    }
    interface Window {
        /** Set by acceptance fallback XHR when DualSelectList did not init. */
        __naisDatagridSettingsSaved?: boolean;
    }
}

function markSettingsSaved(): void {
    reloadOnSettingsClose = true;
    window.__naisDatagridSettingsSaved = true;
}

/** Close settings panel; close hook reloads the page so the datagrid picks up new settings. */
function afterSettingsSaved(): void {
    markSettingsSaved();
    window.naisSideDialog?.close();
}

function ensureSettingsCloseReloadHook(): void {
    if (settingsCloseHookRegistered || typeof window.naisSideDialog === 'undefined') {
        return;
    }

    settingsCloseHookRegistered = true;
    window.naisSideDialog.on('close', () => {
        if (!reloadOnSettingsClose && !window.__naisDatagridSettingsSaved) {
            return;
        }

        reloadOnSettingsClose = false;
        window.__naisDatagridSettingsSaved = false;
        window.location.reload();
    });
}

function syncSettingsHeaderActions(panel: HTMLElement, paneId: string): void {
    panel.querySelectorAll<HTMLElement>('[data-settings-actions]').forEach((group) => {
        const active = group.dataset.settingsActions === paneId;
        group.classList.toggle('is-active', active);
        group.hidden = !active;
    });
}

/** Show header Save/Default for the active settings tab. */
function bindSettingsTabHeaderActions(root: HTMLElement): void {
    const panel = root.closest<HTMLElement>('.nais-sd-panel') ?? root;
    const tabs = panel.querySelector('#gridUserSettingTabs');

    if (!tabs) {
        return;
    }

    $(tabs)
        .off('shown.bs.tab.userSettings')
        .on('shown.bs.tab.userSettings', (event: JQuery.TriggeredEvent) => {
            const href = $(event.target).attr('href') ?? '';
            const paneId = href.startsWith('#') ? href.slice(1) : href;

            if (paneId) {
                syncSettingsHeaderActions(panel, paneId);
            }
        });

    const activePane = panel.querySelector('.tab-pane.active')?.id ?? 'columnSettingsPane';
    syncSettingsHeaderActions(panel, activePane);
}

/**
 * Build DualSelect once per container. afterAjax re-runs initSystemModule on every
 * nette.ajax response — recreating from the page JSON would wipe in-progress edits.
 */
function getOrCreateDualSelectList(
    selector: string,
    options: Record<string, unknown>,
): DualSelectListApi {
    const $target = $(selector);
    const existing = $target.data(DUAL_SELECT_API_DATA_KEY) as DualSelectListApi | undefined;

    if (existing && $target.children('.dsl-panel').length > 0) {
        return existing;
    }

    $target.empty();
    $target.removeData(DUAL_SELECT_API_DATA_KEY);

    const dsl = $.fn.DualSelectList.call($target, options) as DualSelectListApi;
    $target.data(DUAL_SELECT_API_DATA_KEY, dsl);
    initLucideIcons($target.get(0) ?? document);

    return dsl;
}

function collectFilterSettingsRows(
    messages: Pick<SettingsUserConfig, 'filterNameRequired' | 'filterNameDuplicate'>,
): FilterSettingsRow[] | null {
    const rows: FilterSettingsRow[] = [];
    const seenNames = new Set<string>();

    let errorMessage: string | null = null;

    $('#filtersSettingsTable tbody tr').each(function eachFilterRow() {
        const $row = $(this);
        const originalName = String($row.attr('data-filter-name') ?? '');
        const $nameInput = $row.find('.grid-user-setting__filter-name-input');
        const name = $nameInput.length
            ? String($nameInput.val() ?? '').trim()
            : originalName;

        if (!originalName) {
            return;
        }

        if (name === '') {
            errorMessage = messages.filterNameRequired;
            $nameInput.trigger('focus');
            return false;
        }

        const nameKey = name.toLocaleLowerCase();
        if (seenNames.has(nameKey)) {
            errorMessage = messages.filterNameDuplicate;
            $nameInput.trigger('focus');
            return false;
        }
        seenNames.add(nameKey);

        const show = $row.find('.grid-user-setting__filter-show').val() === 'button'
            ? 'button'
            : 'list';
        const $color = $row.find('.grid-user-setting__filter-color');
        const color = $color.length ? String($color.val() ?? '') : null;

        rows.push({originalName, name, show, color});
    });

    if (errorMessage) {
        alert(errorMessage);
        return null;
    }

    return rows;
}

function refreshFilterMoveButtons(): void {
    const $rows = $('#filtersSettingsTable tbody tr');

    $rows.each(function eachFilterRow(index) {
        const $row = $(this);
        $row.find('.grid-user-setting__filter-move-up').prop('disabled', index === 0);
        $row.find('.grid-user-setting__filter-move-down').prop('disabled', index === $rows.length - 1);
    });
}

export function initSettingsUser(_root: HTMLElement): void {
    const configElement = document.getElementById('system-settings-user-config');

    if (!configElement?.textContent) {
        return;
    }

    ensureSettingsCloseReloadHook();
    bindSettingsTabHeaderActions(_root);
    refreshFilterMoveButtons();
    initLucideIcons(_root);

    let config: SettingsUserConfig;

    try {
        config = JSON.parse(configElement.textContent) as SettingsUserConfig;
    } catch {
        return;
    }

    $(document).off('change.userSettings', 'input#selectAll').on('change.userSettings', 'input#selectAll', function onSelectAllChange() {
        const checked = this.checked;
        $(this).closest('.grid-user-setting__fulltext')
            .find('input.checkboxRows')
            .each(function eachRow() {
                window.setNativeChecked?.(this, checked);
            });
    });

    $(document).off('change.userSettings', 'input.checkboxRows').on('change.userSettings', 'input.checkboxRows', function onRowChange() {
        const $fulltext = $(this).closest('.grid-user-setting__fulltext');

        if ($fulltext.length === 0) {
            return;
        }

        let checkAll = true;

        $fulltext.find('input.checkboxRows').each(function eachRow() {
            if (this.checked === false) {
                checkAll = false;
            }
        });

        window.setNativeChecked?.(
            $fulltext.find('input#selectAll')[0],
            checkAll,
            false,
        );
    });

    const dslColors = {
        itemBackground: '#eff6ff',
        itemHoverBackground: '#2563eb',
        itemText: '#111827',
        itemPlaceholderBorder: '#d1d5db',
    };

    const dsl = getOrCreateDualSelectList('#selectRows', {
        candidateItems: config.notSelectedColumns,
        selectionItems: config.selectedColumns,
        moveAll: config.labels.moveAll,
        leftTitle: config.labels.columnsToDisplay,
        rightTitle: config.labels.displayedColumns,
        colors: dslColors,
    });

    $(document).off('click.userSettings');

    $(document).on('click.userSettings', '#saveColumnsSettings', function onSaveColumns() {
        if (dsl.getSelection().length === 0) {
            alert(config.noColsSelected);
            return false;
        }

        const values: string[] = [];

        $('#selectRows .right-panel').find('.dsl-panel-item').each(function eachItem() {
            values.push(String($(this).attr('data-id')));
        });

        $.nette.ajax({
            type: 'post',
            url: config.urls.saveDisplay,
            data: {
                columns: values,
                count: $('#rowsOnPage').val(),
                [config.constants.refreshInterval]: $('#refreshIntervalSelect').val(),
                [config.constants.showRefreshCounter]: $('#showRefreshCounter').is(':checked') ? 1 : 0,
            },
            success() {
                afterSettingsSaved();
            },
        });
    });

    $(document).on('click.userSettings', '#defaultColumnsSettings', function onDefaultColumns() {
        $.nette.ajax({
            type: 'post',
            url: config.urls.saveDisplay,
            data: {
                columns: config.gridColumns.map((column) => column.id),
                count: 10,
                [config.constants.refreshInterval]: 0,
                [config.constants.showRefreshCounter]: 1,
            },
            success() {
                afterSettingsSaved();
            },
        });
    });

    $(document).on('click.userSettings', '#saveFulltextSearch', function onSaveFulltext() {
        const values: string[] = [];

        $('form#fulltextSearch input.checkboxRows').each(function eachCheckbox() {
            if (this.checked) {
                values.push(String($(this).attr('name')));
            }
        });

        $.nette.ajax({
            type: 'post',
            url: config.urls.saveDisplay,
            data: { fulltextColumns: values },
            success() {
                afterSettingsSaved();
            },
        });
    });

    $(document).on('click.userSettings', '#saveFiltersSettings', function onSaveFilters() {
        const filters = collectFilterSettingsRows(config);

        if (filters === null) {
            return false;
        }

        $.nette.ajax({
            type: 'post',
            url: config.urls.saveFilterSettings,
            data: {filters},
            success() {
                afterSettingsSaved();
            },
        });
    });

    $(document).on('click.userSettings', '#defaultFiltersSettings', function onDefaultFilters() {
        $.nette.ajax({
            type: 'post',
            url: config.urls.setFilterSettingsToDefault,
            success() {
                afterSettingsSaved();
            },
        });
    });

    $(document).on('click.userSettings', '#selectRows .dsl-move-all', function onRowsMoveAll() {
        if (dsl.getCandidates().length > 0) {
            dsl.setSelection(dsl.getCandidates());
            dsl.setCandidate();
        } else {
            dsl.setCandidate(dsl.getSelection());
            dsl.setSelection();
        }
    });

    $(document).on(
        'click.userSettings',
        '#filtersSettingsTable .grid-user-setting__filter-delete',
        function onDeleteFilter(event) {
            event.preventDefault();
            $(this).closest('tr').remove();
            refreshFilterMoveButtons();
        },
    );

    $(document).on(
        'click.userSettings',
        '#filtersSettingsTable .grid-user-setting__filter-move-up',
        function onMoveFilterUp(event) {
            event.preventDefault();
            const $row = $(this).closest('tr');
            const $prev = $row.prev('tr');

            if ($prev.length) {
                $row.insertBefore($prev);
                refreshFilterMoveButtons();
            }
        },
    );

    $(document).on(
        'click.userSettings',
        '#filtersSettingsTable .grid-user-setting__filter-move-down',
        function onMoveFilterDown(event) {
            event.preventDefault();
            const $row = $(this).closest('tr');
            const $next = $row.next('tr');

            if ($next.length) {
                $row.insertAfter($next);
                refreshFilterMoveButtons();
            }
        },
    );
}
